import React, { createContext, useContext, useEffect, useState } from 'react';
import CONSTANTS from '../lib/constants';

export type User = {
  email: string;
  scopes: string[];
  localTimezone: 'active' | false | null;
  stores: number[] | string[];
};

export type AuthState = {
  user: User | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
};

type AuthProviderProps = {
  children: React.ReactNode;
};

const API = process.env.REACT_APP_API_NODE_URL;

const AuthCtx = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  async function refresh(): Promise<void> {
    setLoading(true);
    try {
      const res = await fetch(`${API}auth/me`, { credentials: 'include' });
      if (res.ok) {
        const data = (await res.json()) as { email: string; scopes: string[], localTimezone: 'active' | false | null, stores: number[] };
        setUser({ email: data.email, scopes: data.scopes ?? [], localTimezone: data.localTimezone, stores: data.stores });
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  async function logout(): Promise<void> {
    try {
      await fetch(`${API}auth/logout`, { method: 'POST', credentials: 'include' });
    } finally {
      setUser(null);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  const value: AuthState = { user, loading, refresh, logout };

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
